// @flow
import _ from 'lodash'
import type {
  PriceRecommendation,
  Conversion,
  Placement,
  OptionType,
  FbPlacements,
  Configs,
  Conversion as ConversionType,
  AudienceData,
  Options as OptionsType,
  SelectProperties,
  Node,
  LeadformResponse,
  ProviderEstimates,
  VkEventGroup,
  Segment,
  Contact,
  Lookalike,
  ProviderIcons,
  VkEventSources,
  ProviderWorkPosition,
  ProviderTargetingSource,
  AdvertisementStatistics,
  GroupedArrayParameters,
  GroupedObjectParameters,
  GroupedStringParameters,
  AudienceNameTemplate,
  LocationsWithChildren,
  VkGroup,
  GenderType,
  FbAdInterestsType,
  LocalGeoItem
} from '../../../components/types'
import collectAudienceName from './collectAudienceName'
import { Audience } from '../../../components/constants'
import { getDefaultFbPlacements } from '../../../components/AudienceEdit/fbPlacements'
import shortId from 'shortid'
import type { PlacementType } from '../../../components/AudienceEdit/PlacementTypeSelect'
import type { ConsumerPlatformType } from '../../../components/AudienceEdit/fbPlacements'

type Errors = Array<string>

export type CommonPublicProps = {|
  connectionsPath: string,
  publicsPath: string
|}

export type ProviderPublicProps = {|
  connectionsOauthPath: string,
  publicsOauthPath: string,
  getPublicsUrl: string,
  isAccountConnected: boolean,
  isTokenPresent: boolean,
  hasAccountPublicsWithAccess: boolean
|}

export type SocialPublicProps = {|
  common: CommonPublicProps,
  vkProps: ProviderPublicProps,
  fbProps: ProviderPublicProps
|}

export type Initial = {|
  id?: number,
  action: 'copy' | 'update' | 'create',
  isFinishDateShown: boolean,
  isPersisted: boolean,
  isAdmin: boolean,
  isOrtAccounts: boolean,
  currentAdAccountId: number,
  adAccountOptions: Array<OptionType>,

  configs: Configs,

  providers: {
    info: Array<Object>
  },

  conversion: {
    createPath: string,
    value: ?number,
    windowOptions: Array<[string, string]>
  },

  conversions: Array<ConversionType>,

  leadformId: number,
  leadforms: Array<LeadformResponse>,

  audienceData: AudienceData,

  placementType: {
    options: Array<PlacementType>
  },

  includedGroups: Array<VkGroup>,
  excludedGroups: Array<VkGroup>,
  vkActivityGroups: Array<VkGroup>,

  includedVkApps: string,
  excludedVkApps: string,

  relationshipStatus: {
    label: string,
    value: string,
    options: OptionsType
  },

  birthday: {
    label: string,
    value: string,
    options: OptionsType
  },

  paymentType: {
    options: OptionsType,
    value: string
  },

  priceRecommendations: {
    autoBids: Object
  },

  gmap: {
    blogUrl: string,
    scriptSource: string
  },

  localGeo: {
    value: Array<Object>,
    vkType: string,
    tmVisitType: string,
    tmLocType: string,
    fbType: string
  },

  price: {
    value?: string
  },

  statistics: Object,
  defaultEstimates: Object,
  priceConstraints: Object,

  targetingSources: {
    contactTypeTranslations: Object,
    includedSegmentIds: Array<number>,
    excludedSegmentIds: Array<number>,
    includedContactIds: Array<number>,
    excludedContactIds: Array<number>,
    includedLookalikeSegmentIds: Array<number>,
    includedLookalikeContactIds: Array<number>,
    includedProviderTargetingSourceIds: Array<number>,
    excludedProviderTargetingSourceIds: Array<number>,
    segments: Array<Segment>,
    contacts: Array<Contact>,
    lookalikeSegments: Array<Lookalike>,
    lookalikeContacts: Array<Lookalike>,
    providerTargetingSources: Array<ProviderTargetingSource>,
    countries: Array<OptionType>,
    defaultCountryId: number,
    icons: ProviderIcons,
    vkEventOptions: Array<OptionType>,
    vkEventGroups: Array<VkEventGroup>
  },

  locations: {
    includedSelectProperties?: SelectProperties,
    excludedSelectProperties?: SelectProperties,
    preloaded?: Array<OptionType>,
    includedIds: Array<number>,
    excludedIds: Array<number>
  },

  sex: {
    options: OptionsType,
    value?: GenderType
  },

  platform: {
    options: OptionsType,
    value: string,
    label: string
  },

  ageRange: {
    properties?: {
      minAgeLimit: number,
      maxAgeLimit: number
    },
    minAge: number,
    maxAge: number
  },

  pricelist: {
    options: Array<OptionType>,
    value: number,
    name: string
  },

  aboutCompany: {
    label: string,
    value: string
  },

  demographics: {
    providerDemographics: Array<Object>,
    selectedIds: Array<number>
  },

  keywords: {
    value: string
  },

  interests: {
    fbAdInterests: Array<FbAdInterestsType>,
    fbAdInterestsPath: string,
    providerInterests: Array<Node>,
    value: Array<number>
  },

  socialPublics: SocialPublicProps,

  budget: {
    label: string,
    cycleOptions: OptionsType,
    minMonthBudget: number,
    minDailyBudget: number,
    maxBudget: number,
    budgetCycle: string,
    value: string
  },

  budgetDevelopment: {
    label: string,
    options: OptionsType,
    value: string
  },

  finishDate: {
    label: string,
    minDate: string,
    value: string
  },

  showtime: {
    label: string,
    value: Array<Array<number>>
  },

  name: {
    label: string,
    value: string
  },

  campaign: {
    id: number,
    name: string,
    options: Array<OptionType>,
    label: string,
    nameLabel: string
  },

  ageLimit: {
    value: any,
    disabled: string,
    label: string,
    options: OptionsType
  },

  improveAdvertisements: {
    label: string,
    value: boolean
  },

  budgetPortions: {
    providers: Array<string>,
    value: Object
  },

  totalLimit: {
    label: string,
    value: string,
    minimalTotalLimit: number
  },

  estimates: ProviderEstimates,

  legalInformation: {
    disclaimerFinance: boolean,
    financeName: string,
    financeLicenseNumber: string,
    disclaimerMedical: boolean,
    disclaimerSpecialist: boolean,
    disclaimerSupplements: boolean
  },

  onlyTravellers: boolean,
  userDevices: {
    value: Array<number>,
    options: Array<OptionType>
  },

  vkPositions: Array<ProviderWorkPosition>,
  fbPositions: Array<ProviderWorkPosition>,
  audienceNameTemplates: Array<AudienceNameTemplate>,
  selectedAudienceNameTemplateId: number | null,
  vkBiddingMode: string,
  tmBiddingMode: string,
  fbBiddingMode: string,
  tmShowsLimitInfo: {
    showsPeriod: string,
    audienceShowsLimit: number,
    adShowsLimit: number
  },
  vkShowsLimit: number,
  status: string
|}

type Data = {|
  initialized: boolean,
  name: string,
  audienceType: Placement,
  priceRecommendationsIsLoading: boolean,
  priceRecommendations: Array<PriceRecommendation>,
  providers: Array<string>,
  paymentType: string,
  placementTypes: Array<PlacementType>,
  sex: GenderType,
  includedLocationGroups: Array<GroupedArrayParameters>,
  excludedLocationIds: Array<number>,
  preloadedLocations: Array<Object>,
  minAge: number | null,
  maxAge: number | null,
  platform: ConsumerPlatformType,
  vkPublic: Object,
  fbPublic: Object,
  includedGroups: Array<GroupedObjectParameters>,
  excludedGroups: Array<VkGroup>,
  vkActivityGroups: Array<GroupedObjectParameters>,
  loadedIncludedGroups: Array<VkGroup>,
  loadedExcludedGroups: Array<VkGroup>,
  loadedVkActivityGroups: Array<VkGroup>,
  includedVkAppsGroups: Array<GroupedStringParameters>,
  excludedVkApps: string,
  birthday?: string,
  relationshipStatus?: string,
  ageLimit: string,
  improveAdvertisements: boolean,
  isAdminFieldShown: boolean,
  budgetPortions: Object,
  finishDate: string | void,
  keywordsGroups: Array<GroupedStringParameters>,
  interestIds: Array<number>,
  fbAdInterests: Array<FbAdInterestsType>,
  providerInterests: Array<Node>,
  demographicIds: Array<number>,
  fbPlacements: FbPlacements,
  vkPlacement: string,
  budget: string,
  budgetCycle: string,
  budgetDevelopment: string,
  price: string,
  showtime: Array<Array<number>>,
  localGeo: Array<LocalGeoItem>,
  vkLocalGeoType: string,
  tmLocalGeoVisitType: string,
  tmLocalGeoLocType: string,
  fbLocalGeoType: string,
  geographyType: string,
  aboutCompany: string,
  includedSegmentIds: Array<number>,
  excludedSegmentIds: Array<number>,
  includedContactIds: Array<number>,
  excludedContactIds: Array<number>,
  includedLookalikeSegmentIds: Array<number>,
  includedLookalikeContactIds: Array<number>,
  includedProviderTargetingSourceIds: Array<number>,
  excludedProviderTargetingSourceIds: Array<number>,
  segments: Array<Segment>,
  contacts: Array<Contact>,
  lookalikeSegments: Array<Lookalike>,
  lookalikeContacts: Array<Lookalike>,
  providerTargetingSources: Array<ProviderTargetingSource>,
  countries: Array<OptionType>,
  defaultCountryId: number,
  campaignId: number,
  campaignName: string,
  pricelistId: number | '',
  isLoading: boolean,
  conversionId: ?number,
  leadformId: ?number,
  conversions: Array<Conversion>,
  initial: Initial | null,
  prefixName: string,
  providersInfo: Array<Object>,
  adAccountOptions: Array<OptionType>,
  currentAdAccountId: number,
  campaignOptions: Array<OptionType>,
  totalLimit: string,
  disclaimerFinance: boolean,
  financeName: string,
  financeLicenseNumber: string,
  disclaimerMedical: boolean,
  disclaimerSpecialist: boolean,
  disclaimerSupplements: boolean,
  vkEventOptions: Array<OptionType>,
  vkEventGroups: Array<VkEventGroup>,
  vkEventSources: VkEventSources | Object,
  onlyTravellers: boolean,
  vkUserDevices: Array<number>,
  vkPositions: Array<ProviderWorkPosition>,
  fbPositions: Array<ProviderWorkPosition>,
  adStatistics: { [number]: AdvertisementStatistics },
  useLocationGroupNames: boolean,
  useVkGroupNames: boolean,
  useVkAppsGroupNames: boolean,
  useKeywordsGroupNames: boolean,
  useVkActivityGroupNames: boolean,
  audienceNameTemplates: Array<AudienceNameTemplate>,
  selectedAudienceNameTemplateId: number | null,
  vkBiddingMode: string,
  tmBiddingMode: string,
  fbBiddingMode: string,
  tmShowsPeriod: string,
  tmAudienceShowsLimit: number,
  tmAdShowsLimit: number,
  vkShowsLimit: number,
  locationsWithChildren: LocationsWithChildren,
  status: string,
  imported: boolean
|}

type ValidationErrors = {|
  providersErrors: Errors,
  placementsErrors: Errors,
  vkPlacementErrors: Errors,
  includedGroupsErrors: Errors,
  excludedGroupsErrors: Errors,
  vkActivityGroupsErrors: Errors,
  includedVkAppsErrors: Errors,
  excludedVkAppsErrors: Errors,
  relationshipStatusErrors: Errors,
  birthdayErrors: Errors,
  localGeoErrors: Errors,
  priceErrors: Errors,
  includedSegmentIdsErrors: Errors,
  excludedSegmentIdsErrors: Errors,
  includedContactIdsErrors: Errors,
  excludedContactIdsErrors: Errors,
  includedLookalikeSegmentIdsErrors: Errors,
  includedLookalikeContactIdsErrors: Errors,
  includedProviderTargetingSourceIdsErrors: Errors,
  excludedProviderTargetingSourceIdsErrors: Errors,
  platformErrors: Errors,
  aboutCompanyErrors: Errors,
  demographicErrors: Errors,
  keywordsErrors: Errors,
  interestsErrors: Errors,
  vkPublicErrors: Errors,
  fbPublicErrors: Errors,
  budgetErrors: Errors,
  finishDateErrors: Errors,
  showtimeErrors: Errors,
  nameErrors: Errors,
  campaignErrors: Errors,
  campaignNameErrors: Errors,
  ageLimitErrors: Errors,
  improveAdvertisementsErrors: Errors,
  ageErrors: Errors,
  includedLocationsErrors: Errors,
  excludedLocationsErrors: Errors,
  advertisementsErrors: Errors,
  pricelistErrors: Errors,
  conversionIdErrors: Errors,
  leadformIdErrors: Errors,
  adAccountErrors: Errors,
  totalLimitErrors: Errors,
  financeNameErrors: Errors,
  financeLicenseNumberErrors: Errors,
  vkEventGroupsErrors: Errors
|}

type State = {| ...Data, ...ValidationErrors |}

type PatchAction = {|
  type: 'AUDIENCE_EDIT.PATCH',
  state: $Shape<State>,
  segmentation: Object
|}

type InitialAction = {|
  type: 'AUDIENCE_EDIT.INIT',
  state: Initial
|}

type Action = PatchAction | InitialAction

export type { State, Errors, ValidationErrors }

export const initialErrors: ValidationErrors = {
  providersErrors: [],
  placementsErrors: [],
  vkPlacementErrors: [],
  includedGroupsErrors: [],
  excludedGroupsErrors: [],
  vkActivityGroupsErrors: [],
  includedVkAppsErrors: [],
  excludedVkAppsErrors: [],
  relationshipStatusErrors: [],
  birthdayErrors: [],
  localGeoErrors: [],
  priceErrors: [],
  includedSegmentIdsErrors: [],
  excludedSegmentIdsErrors: [],
  includedContactIdsErrors: [],
  excludedContactIdsErrors: [],
  includedLookalikeSegmentIdsErrors: [],
  includedLookalikeContactIdsErrors: [],
  includedProviderTargetingSourceIdsErrors: [],
  excludedProviderTargetingSourceIdsErrors: [],
  platformErrors: [],
  aboutCompanyErrors: [],
  demographicErrors: [],
  keywordsErrors: [],
  interestsErrors: [],
  vkPublicErrors: [],
  fbPublicErrors: [],
  budgetErrors: [],
  finishDateErrors: [],
  showtimeErrors: [],
  nameErrors: [],
  campaignErrors: [],
  campaignNameErrors: [],
  ageLimitErrors: [],
  improveAdvertisementsErrors: [],
  ageErrors: [],
  includedLocationsErrors: [],
  excludedLocationsErrors: [],
  advertisementsErrors: [],
  pricelistErrors: [],
  conversionIdErrors: [],
  leadformIdErrors: [],
  adAccountErrors: [],
  totalLimitErrors: [],
  financeNameErrors: [],
  financeLicenseNumberErrors: [],
  vkEventGroupsErrors: []
}

export const errorKeys: Array<string> = _.keys(initialErrors)

const initialData: Data = {
  initialized: false,
  name: '',
  audienceType: 'targeting',
  providers: [],
  paymentType: '',
  placementTypes: ['promo_post'],
  sex: 'both',
  includedLocationGroups: [],
  excludedLocationIds: [],
  preloadedLocations: [],
  minAge: null,
  maxAge: null,
  platform: 'all',
  vkPublic: {},
  fbPublic: {},
  includedGroups: [{ value: [], name: '' }],
  excludedGroups: [],
  vkActivityGroups: [{ value: [], name: '' }],
  loadedIncludedGroups: [],
  loadedExcludedGroups: [],
  loadedVkActivityGroups: [],
  includedVkAppsGroups: [{ value: '', name: '' }],
  excludedVkApps: '',
  birthday: '',
  relationshipStatus: '',
  ageLimit: '',
  improveAdvertisements: false,
  isAdminFieldShown: false,
  budgetPortions: {},
  finishDate: undefined,
  keywordsGroups: [{ value: '', name: '' }],
  interestIds: [],
  fbAdInterests: [],
  providerInterests: [],
  demographicIds: [],
  fbPlacements: {},
  vkPlacement: '',
  budget: '',
  budgetCycle: '',
  budgetDevelopment: '',
  price: '',
  showtime: [],
  localGeo: [],
  vkLocalGeoType: '',
  tmLocalGeoVisitType: '',
  tmLocalGeoLocType: '',
  fbLocalGeoType: '',
  geographyType: 'cities',
  aboutCompany: '',
  includedSegmentIds: [],
  excludedSegmentIds: [],
  includedContactIds: [],
  excludedContactIds: [],
  includedLookalikeSegmentIds: [],
  includedLookalikeContactIds: [],
  includedProviderTargetingSourceIds: [],
  excludedProviderTargetingSourceIds: [],
  segments: [],
  contacts: [],
  lookalikeSegments: [],
  lookalikeContacts: [],
  providerTargetingSources: [],
  countries: [],
  defaultCountryId: 0,
  campaignId: 0,
  campaignName: '',
  pricelistId: '',
  priceRecommendations: [],
  priceRecommendationsIsLoading: false,
  isLoading: false,
  conversionId: null,
  conversions: [],
  leadformId: null,
  initial: null,
  prefixName: '',
  providersInfo: [],
  adAccountOptions: [],
  currentAdAccountId: 0,
  campaignOptions: [],
  totalLimit: '',
  disclaimerFinance: false,
  financeName: '',
  financeLicenseNumber: '',
  disclaimerMedical: false,
  disclaimerSpecialist: false,
  disclaimerSupplements: false,
  vkEventOptions: [],
  vkEventGroups: [],
  vkEventSources: {},
  onlyTravellers: false,
  vkUserDevices: [],
  vkPositions: [],
  fbPositions: [],
  adStatistics: {},
  useLocationGroupNames: false,
  useVkGroupNames: false,
  useVkAppsGroupNames: false,
  useKeywordsGroupNames: false,
  useVkActivityGroupNames: false,
  audienceNameTemplates: [],
  selectedAudienceNameTemplateId: null,
  vkBiddingMode: '',
  tmBiddingMode: '',
  fbBiddingMode: '',
  tmShowsPeriod: '',
  tmAudienceShowsLimit: 0,
  tmAdShowsLimit: 0,
  vkShowsLimit: 0,
  locationsWithChildren: ({}: Object),
  status: 'no_active_ads',
  imported: false
}

export const initialState = { ...initialData, ...initialErrors }

export function init(state: Initial): Action {
  return {
    type: 'AUDIENCE_EDIT.INIT',
    state
  }
}

export function patch(state: $Shape<State>, segmentation: Object): Action {
  return {
    type: 'AUDIENCE_EDIT.PATCH',
    state,
    segmentation
  }
}

function fromInitial(initial: Initial): State {
  const preloadedLocations = initial.locations.preloaded
    ? initial.locations.preloaded.map((location) => {
        return { id: location.id, value: location.id, label: location.name }
      })
    : []

  return {
    ...initialErrors,
    initialized: true,
    name: initial.name.value,
    audienceType: initial.audienceData.audienceType,
    priceRecommendationsIsLoading: initial.audienceData.audienceType === Audience.types.targeting,
    providers: _.filter(initial.audienceData.providers, (provider) =>
      _.find(initial.providers.info, { id: provider })
    ),
    paymentType: initial.paymentType.value,
    placementTypes: [initial.audienceData.placementType],
    sex: initial.sex.value || 'both',
    includedLocationGroups: [{ value: initial.locations.includedIds, name: '' }],
    excludedLocationIds: initial.locations.excludedIds,
    preloadedLocations,
    minAge: initial.ageRange.minAge,
    maxAge: initial.ageRange.maxAge,
    platform: (initial.platform.value: any),
    vkPublic: initial.audienceData.vkPublic,
    fbPublic: initial.audienceData.fbPublic,
    includedGroups: [{ value: initial.includedGroups || [], name: '' }],
    excludedGroups: initial.excludedGroups || [],
    vkActivityGroups: [{ value: initial.vkActivityGroups || [], name: '' }],
    loadedIncludedGroups: initial.includedGroups || [],
    loadedExcludedGroups: initial.excludedGroups || [],
    loadedVkActivityGroups: initial.vkActivityGroups || [],
    includedVkAppsGroups: [{ value: initial.includedVkApps || '', name: '' }],
    excludedVkApps: initial.excludedVkApps || '',
    birthday: initial.birthday && initial.birthday.value,
    relationshipStatus: initial.relationshipStatus && initial.relationshipStatus.value,
    ageLimit: initial.ageLimit.value,
    improveAdvertisements: initial.improveAdvertisements.value,
    isAdminFieldShown: false,
    budgetPortions: (initial.budgetPortions && initial.budgetPortions.value) || {},
    finishDate: initial.finishDate.value || '',
    keywordsGroups: [{ value: (initial.keywords && initial.keywords.value) || '', name: '' }],
    interestIds: initial.interests && initial.interests.value,
    fbAdInterests: (initial.interests && initial.interests.fbAdInterests) || [],
    providerInterests: (initial.interests && initial.interests.providerInterests) || [],
    demographicIds: initial.demographics && initial.demographics.selectedIds,
    fbPlacements: _.isEmpty(initial.audienceData.fbPlacements)
      ? getDefaultFbPlacements(
          initial.audienceData.placementType,
          initial.audienceData.audienceType,
          (initial.platform.value: any)
        )
      : initial.audienceData.fbPlacements,
    vkPlacement: initial.audienceData.vkPlacement || Audience.vk_placements.default_vk_placement,
    budget:
      (initial.budget &&
        !_.isEmpty(initial.budget.value) &&
        parseInt(initial.budget.value).toString()) ||
      '',
    budgetCycle: initial.budget.budgetCycle,
    budgetDevelopment: initial.budgetDevelopment && initial.budgetDevelopment.value,
    price: initial.price.value || '',
    showtime: initial.showtime.value,
    localGeo: initial.localGeo && initial.localGeo.value,
    vkLocalGeoType: (initial.localGeo && initial.localGeo.vkType) || 'regular',
    tmLocalGeoVisitType: (initial.localGeo && initial.localGeo.tmVisitType) || 'usual',
    tmLocalGeoLocType: (initial.localGeo && initial.localGeo.tmLocType) || 'all',
    fbLocalGeoType: (initial.localGeo && initial.localGeo.fbType) || 'all',
    geographyType: _.isEmpty(initial.localGeo && initial.localGeo.value) ? 'cities' : 'micro',
    aboutCompany: initial.aboutCompany.value,
    includedSegmentIds:
      (initial.targetingSources && initial.targetingSources.includedSegmentIds) || [],
    excludedSegmentIds:
      (initial.targetingSources && initial.targetingSources.excludedSegmentIds) || [],
    includedContactIds:
      (initial.targetingSources && initial.targetingSources.includedContactIds) || [],
    excludedContactIds:
      (initial.targetingSources && initial.targetingSources.excludedContactIds) || [],
    includedLookalikeSegmentIds:
      (initial.targetingSources && initial.targetingSources.includedLookalikeSegmentIds) || [],
    includedLookalikeContactIds:
      (initial.targetingSources && initial.targetingSources.includedLookalikeContactIds) || [],
    includedProviderTargetingSourceIds:
      (initial.targetingSources && initial.targetingSources.includedProviderTargetingSourceIds) ||
      [],
    excludedProviderTargetingSourceIds:
      (initial.targetingSources && initial.targetingSources.excludedProviderTargetingSourceIds) ||
      [],
    segments: (initial.targetingSources && initial.targetingSources.segments) || [],
    contacts: (initial.targetingSources && initial.targetingSources.contacts) || [],
    lookalikeSegments:
      (initial.targetingSources && initial.targetingSources.lookalikeSegments) || [],
    lookalikeContacts:
      (initial.targetingSources && initial.targetingSources.lookalikeContacts) || [],
    providerTargetingSources:
      (initial.targetingSources && initial.targetingSources.providerTargetingSources) || [],
    campaignId: _.includes(_.map(initial.campaign.options, 'id'), initial.campaign.id)
      ? initial.campaign.id || 0
      : 0,
    advertisementsErrors: [],
    pricelistId: (initial.pricelist && initial.pricelist.value) || '',
    campaignName: initial.campaign.name || '',
    priceRecommendations: [],
    isLoading: false,
    conversionId: initial.conversion && initial.conversion.value,
    conversions: initial.conversions,
    leadformId: initial.leadformId,
    countries: (initial.targetingSources && initial.targetingSources.countries) || [],
    defaultCountryId: (initial.targetingSources && initial.targetingSources.defaultCountryId) || 0,
    initial,
    prefixName: '',
    providersInfo: initial.providers.info,
    adAccountOptions: initial.adAccountOptions || [],
    currentAdAccountId: initial.currentAdAccountId,
    campaignOptions: initial.campaign.options || [],
    totalLimit:
      (initial.totalLimit &&
        !_.isEmpty(initial.totalLimit.value) &&
        parseInt(initial.totalLimit.value).toString()) ||
      '',
    disclaimerFinance: initial.legalInformation.disclaimerFinance,
    financeName: initial.legalInformation.financeName,
    financeLicenseNumber: initial.legalInformation.financeLicenseNumber,
    disclaimerMedical: initial.legalInformation.disclaimerMedical,
    disclaimerSpecialist: initial.legalInformation.disclaimerSpecialist,
    disclaimerSupplements: initial.legalInformation.disclaimerSupplements,
    vkEventOptions: (initial.targetingSources && initial.targetingSources.vkEventOptions) || [],
    vkEventGroups: (initial.targetingSources && initial.targetingSources.vkEventGroups) || [],
    vkEventSources: (initial.targetingSources && buildSources(initial.targetingSources)) || {},
    onlyTravellers: initial.onlyTravellers,
    vkUserDevices: (initial.userDevices && initial.userDevices.value) || [],
    vkPositions: initial.vkPositions || [],
    fbPositions: initial.fbPositions || [],
    adStatistics: {},
    useLocationGroupNames: false,
    useVkGroupNames: false,
    useVkAppsGroupNames: false,
    useKeywordsGroupNames: false,
    useVkActivityGroupNames: false,
    audienceNameTemplates: initial.audienceNameTemplates || [],
    selectedAudienceNameTemplateId: initial.selectedAudienceNameTemplateId || null,
    vkBiddingMode: initial.vkBiddingMode || 'fixed',
    tmBiddingMode: initial.tmBiddingMode || 'max_amount',
    fbBiddingMode: initial.fbBiddingMode || 'max_amount',
    tmShowsPeriod: (initial.tmShowsLimitInfo && initial.tmShowsLimitInfo.showsPeriod) || 'week',
    tmAudienceShowsLimit:
      initial.tmShowsLimitInfo &&
      (initial.tmShowsLimitInfo.audienceShowsLimit ||
        initial.tmShowsLimitInfo.audienceShowsLimit === 0)
        ? initial.tmShowsLimitInfo.audienceShowsLimit
        : 2,
    tmAdShowsLimit:
      initial.tmShowsLimitInfo &&
      (initial.tmShowsLimitInfo.adShowsLimit || initial.tmShowsLimitInfo.adShowsLimit === 0)
        ? initial.tmShowsLimitInfo.adShowsLimit
        : 20,
    vkShowsLimit: initial.vkShowsLimit || 2,
    locationsWithChildren: ({}: Object),
    status: initial.audienceData.status,
    imported: initial.action === 'copy' ? false : initial.audienceData.imported
  }
}

function buildSources(targetingSources: Object): VkEventSources | Object {
  const { segments, contacts } = targetingSources
  let sources = {}
  _.each(segments, (segment) => {
    sources[shortId.generate()] = { ...segment, type: 'segment' }
  })
  _.each(contacts, (contact) => {
    sources[shortId.generate()] = { ...contact, type: 'contact' }
  })
  return sources
}

function getCorrectName(state: State, nextState: State, typedAction: PatchAction): string {
  const prevAutoName = collectAudienceName(state, typedAction.segmentation)
  const nextAutoName = collectAudienceName(nextState, typedAction.segmentation)
  if (state.name !== nextState.name) return nextState.name
  return prevAutoName === nextAutoName ? nextState.name : nextAutoName
}

export default function reducer(state: State = initialState, action: Action): State {
  switch (action.type) {
    case 'AUDIENCE_EDIT.INIT': {
      return fromInitial(action.state)
    }
    case 'AUDIENCE_EDIT.PATCH': {
      const typedAction: PatchAction = action
      const nextState = { ...state, ...typedAction.state }
      return { ...nextState, name: getCorrectName(state, nextState, typedAction) }
    }
    default:
      return state
  }
}
